import React, { useState, useCallback, useContext, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import {
	Button,
	TableBody,
	TableRow,
	TableCell,
	IconButton,
	Table,
	TableHead,
	Paper,
	Tooltip,
	Typography,
	CircularProgress,
	Avatar,
	Switch,
} from "@material-ui/core";
import {
	Edit,
	CheckCircle,
	SignalCellularConnectedNoInternet2Bar,
	SignalCellularConnectedNoInternet0Bar,
	SignalCellular4Bar,
	CropFree,
	DeleteOutline,
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";

import api from "../../services/api";
import WhatsAppModal from "../../components/WhatsAppModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import QrcodeModal from "../../components/QrcodeModal";
import { i18n } from "../../translate/i18n";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
	mainPaper: {
		flex: 1,
		padding: theme.spacing(1),
		overflowY: "scroll",
		...theme.scrollbarStyles,
	},
	customTableCell: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	},
	tooltip: {
		backgroundColor: "#f5f5f9",
		color: "rgba(0, 0, 0, 0.87)",
		fontSize: theme.typography.pxToRem(14),
		border: "1px solid #dadde9",
		maxWidth: 450,
	},
	tooltipPopper: {
		textAlign: "center",
	},
	buttonProgress: {
		color: green[500],
	},
	profileContainer: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		gap: theme.spacing(1),
	},
	profileAvatar: {
		border: `2px solid ${green[500]}`,
		width: 40,
		height: 40,
	},
	profileInfo: {
		display: "flex",
		flexDirection: "column",
		alignItems: "flex-start",
	},
}));

const CustomToolTip = ({ title, content, children }) => {
	const classes = useStyles();

	return (
		<Tooltip
			arrow
			classes={{
				tooltip: classes.tooltip,
				popper: classes.tooltipPopper,
			}}
			title={
				<React.Fragment>
					<Typography gutterBottom color="inherit">
						{title}
					</Typography>
					{content && <Typography>{content}</Typography>}
				</React.Fragment>
			}
		>
			{children}
		</Tooltip>
	);
};

const Connections = () => {
	const classes = useStyles();

	const { whatsApps, loading } = useContext(WhatsAppsContext);
	const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
	const [qrModalOpen, setQrModalOpen] = useState(false);
	const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const [localWhatsApps, setLocalWhatsApps] = useState([]);
	const pollingRef = useRef();
	const selectedWhatsAppRef = useRef();
	const qrModalOpenRef = useRef();

	useEffect(() => {
		setLocalWhatsApps(whatsApps);
	}, [whatsApps]);

	// Keep refs in sync
	useEffect(() => {
		selectedWhatsAppRef.current = selectedWhatsApp;
	}, [selectedWhatsApp]);

	useEffect(() => {
		qrModalOpenRef.current = qrModalOpen;
	}, [qrModalOpen]);

	useEffect(() => {
		const pollStatus = async () => {
			if (!localWhatsApps || localWhatsApps.length === 0) return;
			const updatedWhatsApps = await Promise.all(
				localWhatsApps.map(async (wa) => {
					try {
						const { data } = await api.get(`/whatsapp/${wa.id}/status`);
						// Preserve existing profile if new data doesn't have it and status is still CONNECTED
						if (data.status === "CONNECTED" && !data.profile && wa.profile) {
							data.profile = wa.profile;
						}
						return data;
					} catch (err) {
						return wa;
					}
				})
			);
			
			setLocalWhatsApps(updatedWhatsApps);
			
			// Check if QR modal is open and update QR code if it changed
			const currentSelected = selectedWhatsAppRef.current;
			const isQrModalOpen = qrModalOpenRef.current;
			if (isQrModalOpen && currentSelected) {
				const updatedWhatsApp = updatedWhatsApps.find(wa => wa.id === currentSelected.id);
				if (updatedWhatsApp) {
					// Update QR code if it changed
					if (updatedWhatsApp.qrcode && updatedWhatsApp.qrcode !== currentSelected.qrcode) {
						setSelectedWhatsApp(updatedWhatsApp);
					}
					// Close modal if status changed to CONNECTED
					if (updatedWhatsApp.status === "CONNECTED" && currentSelected.status !== "CONNECTED") {
						setQrModalOpen(false);
						setSelectedWhatsApp(null);
					}
				}
			}
		};
		pollingRef.current = setInterval(pollStatus, 2000);
		pollStatus(); // initial fetch
		return () => clearInterval(pollingRef.current);
	}, [localWhatsApps.length]);

	const confirmationModalInitialState = {
		action: "",
		title: "",
		message: "",
		whatsAppId: "",
		open: false,
	};
	const [confirmModalInfo, setConfirmModalInfo] = useState(
		confirmationModalInitialState
	);

	const handleStartWhatsAppSession = async whatsAppId => {
		try {
			await api.post(`/whatsappsession/${whatsAppId}`);
		} catch (err) {
			toastError(err);
		}
	};

	const handleRequestNewQrCode = async whatsAppId => {
		try {
			await api.put(`/whatsappsession/${whatsAppId}`);
		} catch (err) {
			toastError(err);
		}
	};

	const handleOpenWhatsAppModal = () => {
		setSelectedWhatsApp(null);
		setWhatsAppModalOpen(true);
	};

	const handleCloseWhatsAppModal = useCallback(() => {
		setWhatsAppModalOpen(false);
		setSelectedWhatsApp(null);
	}, [setSelectedWhatsApp, setWhatsAppModalOpen]);

	const handleOpenQrModal = whatsApp => {
		setSelectedWhatsApp(whatsApp);
		setQrModalOpen(true);
	};

	const handleCloseQrModal = useCallback(() => {
		setSelectedWhatsApp(null);
		setQrModalOpen(false);
	}, [setQrModalOpen, setSelectedWhatsApp]);

	const handleEditWhatsApp = whatsApp => {
		setSelectedWhatsApp(whatsApp);
		setWhatsAppModalOpen(true);
	};

	const handleOpenConfirmationModal = (action, whatsAppId) => {
		if (action === "disconnect") {
			setConfirmModalInfo({
				action: action,
				title: i18n.t("connections.confirmationModal.disconnectTitle"),
				message: i18n.t("connections.confirmationModal.disconnectMessage"),
				whatsAppId: whatsAppId,
			});
		}

		if (action === "delete") {
			setConfirmModalInfo({
				action: action,
				title: i18n.t("connections.confirmationModal.deleteTitle"),
				message: i18n.t("connections.confirmationModal.deleteMessage"),
				whatsAppId: whatsAppId,
			});
		}
		setConfirmModalOpen(true);
	};

	const handleSubmitConfirmationModal = async () => {
		if (confirmModalInfo.action === "disconnect") {
			try {
				await api.delete(`/whatsappsession/${confirmModalInfo.whatsAppId}`);
			} catch (err) {
				toastError(err);
			}
		}

		if (confirmModalInfo.action === "delete") {
			try {
				await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}`);
				toast.success(i18n.t("connections.toasts.deleted"));
			} catch (err) {
				toastError(err);
			}
		}

		setConfirmModalInfo(confirmationModalInitialState);
	};

	const renderActionButtons = whatsApp => {
		return (
			<>
				{whatsApp.status === "qrcode" && (
					<Button
						size="small"
						variant="contained"
						color="primary"
						onClick={() => handleOpenQrModal(whatsApp)}
					>
						{i18n.t("connections.buttons.qrcode")}
					</Button>
				)}
				{whatsApp.status === "DISCONNECTED" && (
					<>
						<Button
							size="small"
							variant="outlined"
							color="primary"
							onClick={() => handleStartWhatsAppSession(whatsApp.id)}
						>
							{i18n.t("connections.buttons.tryAgain")}
						</Button>{" "}
						<Button
							size="small"
							variant="outlined"
							color="secondary"
							onClick={() => handleRequestNewQrCode(whatsApp.id)}
						>
							{i18n.t("connections.buttons.newQr")}
						</Button>
					</>
				)}
				{whatsApp.status === "OPENING" && (
					<Button size="small" variant="outlined" disabled color="default">
						{i18n.t("connections.buttons.connecting")}
					</Button>
				)}
				<Button
					size="small"
					variant="outlined"
					color="secondary"
					disabled={whatsApp.status === "DISCONNECTED" || whatsApp.status === "OPENING"}
					onClick={() => {
						handleOpenConfirmationModal("disconnect", whatsApp.id);
					}}
				>
					{i18n.t("connections.buttons.disconnect")}
				</Button>
			</>
		);
	};

	const renderStatusToolTips = whatsApp => {
		return (
			<div className={classes.customTableCell}>
				{whatsApp.status === "DISCONNECTED" && (
					<CustomToolTip
						title={i18n.t("connections.toolTips.disconnected.title")}
						content={i18n.t("connections.toolTips.disconnected.content")}
					>
						<SignalCellularConnectedNoInternet0Bar color="secondary" />
					</CustomToolTip>
				)}
				{whatsApp.status === "OPENING" && (
					<CircularProgress size={24} className={classes.buttonProgress} />
				)}
				{whatsApp.status === "qrcode" && (
					<CustomToolTip
						title={i18n.t("connections.toolTips.qrcode.title")}
						content={i18n.t("connections.toolTips.qrcode.content")}
					>
						<CropFree />
					</CustomToolTip>
				)}
				{whatsApp.status === "CONNECTED" && (
					<CustomToolTip title={i18n.t("connections.toolTips.connected.title")}>
						<SignalCellular4Bar style={{ color: green[500] }} />
					</CustomToolTip>
				)}
				{(whatsApp.status === "TIMEOUT" || whatsApp.status === "PAIRING") && (
					<CustomToolTip
						title={i18n.t("connections.toolTips.timeout.title")}
						content={i18n.t("connections.toolTips.timeout.content")}
					>
						<SignalCellularConnectedNoInternet2Bar color="secondary" />
					</CustomToolTip>
				)}
			</div>
		);
	};

	return (
		<MainContainer>
			<ConfirmationModal
				title={confirmModalInfo.title}
				open={confirmModalOpen}
				onClose={setConfirmModalOpen}
				onConfirm={handleSubmitConfirmationModal}
			>
				{confirmModalInfo.message}
			</ConfirmationModal>
			<QrcodeModal
				open={qrModalOpen}
				onClose={handleCloseQrModal}
				whatsAppId={!whatsAppModalOpen && selectedWhatsApp?.id}
				qrcode={selectedWhatsApp?.qrcode}
			/>
			<WhatsAppModal
				open={whatsAppModalOpen}
				onClose={handleCloseWhatsAppModal}
				whatsAppId={!qrModalOpen && selectedWhatsApp?.id}
			/>
			<MainHeader>
				<Title>{i18n.t("connections.title")}</Title>
				<MainHeaderButtonsWrapper>
					<Button
						variant="contained"
						color="primary"
						onClick={handleOpenWhatsAppModal}
					>
						{i18n.t("connections.buttons.add")}
					</Button>
				</MainHeaderButtonsWrapper>
			</MainHeader>
			<Paper className={classes.mainPaper} variant="outlined">
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell align="center">
								{i18n.t("connections.table.name")}
							</TableCell>
							<TableCell align="center">
								Perfil
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.status")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.session")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.lastUpdate")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.default")}
							</TableCell>
							<TableCell align="center">
								Chatbot
							</TableCell>
							<TableCell align="center">
								Habilitar Chats
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.actions")}
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{loading ? (
							<TableRowSkeleton />
						) : (
							<>
								{localWhatsApps?.length > 0 &&
									localWhatsApps.map(whatsApp => (
										<TableRow key={whatsApp.id}>
											<TableCell align="center">
												{whatsApp.name}
											</TableCell>
											<TableCell align="center">
												{whatsApp.status === "CONNECTED" && whatsApp.profile ? (
													<div className={classes.profileContainer}>
														{whatsApp.profile.picture && (
															<Avatar
																src={whatsApp.profile.picture}
																alt={whatsApp.profile.name || whatsApp.profile.number}
																className={classes.profileAvatar}
															/>
														)}
														<div className={classes.profileInfo}>
															<Typography variant="body2">
																{whatsApp.profile.name || whatsApp.profile.number || "-"}
															</Typography>
															{whatsApp.profile.name && whatsApp.profile.number && (
																<Typography variant="caption" color="textSecondary">
																	{whatsApp.profile.number}
																</Typography>
															)}
														</div>
													</div>
												) : (
													"-"
												)}
											</TableCell>
											<TableCell align="center">
												{renderStatusToolTips(whatsApp)}
											</TableCell>
											<TableCell align="center">
												{renderActionButtons(whatsApp)}
											</TableCell>
											<TableCell align="center">
												{format(parseISO(whatsApp.updatedAt), "dd/MM/yy HH:mm")}
											</TableCell>
											<TableCell align="center">
												{whatsApp.isDefault && (
													<div className={classes.customTableCell}>
														<CheckCircle style={{ color: green[500] }} />
													</div>
												)}
											</TableCell>
											<TableCell align="center">
												<Switch
													checked={whatsApp.enableChatbot || false}
													onChange={async (e) => {
														const newValue = e.target.checked;
														try {
															// Only send the fields we need to update
															const response = await api.put(`/whatsapp/${whatsApp.id}`, {
																name: whatsApp.name,
																greetingMessage: whatsApp.greetingMessage,
																farewellMessage: whatsApp.farewellMessage,
																isDefault: whatsApp.isDefault,
																enableChatbot: newValue,
																habilitar_chats: whatsApp.habilitar_chats !== undefined ? whatsApp.habilitar_chats : true,
																queueIds: whatsApp.queues?.map(q => q.id) || []
															});
															// Update local state immediately
															setLocalWhatsApps(prev => 
																prev.map(w => 
																	w.id === whatsApp.id 
																		? { ...w, enableChatbot: newValue }
																		: w
																)
															);
															toast.success("Chatbot " + (newValue ? "habilitado" : "deshabilitado"));
														} catch (err) {
															toastError(err);
														}
													}}
													color="primary"
													size="small"
												/>
											</TableCell>
											<TableCell align="center">
												<Switch
													checked={whatsApp.habilitar_chats !== undefined ? whatsApp.habilitar_chats : true}
													onChange={async (e) => {
														const newValue = e.target.checked;
														try {
															// Only send the fields we need to update
															const response = await api.put(`/whatsapp/${whatsApp.id}`, {
																name: whatsApp.name,
																greetingMessage: whatsApp.greetingMessage,
																farewellMessage: whatsApp.farewellMessage,
																isDefault: whatsApp.isDefault,
																enableChatbot: whatsApp.enableChatbot || false,
																habilitar_chats: newValue,
																queueIds: whatsApp.queues?.map(q => q.id) || []
															});
															// Update local state immediately
															setLocalWhatsApps(prev => 
																prev.map(w => 
																	w.id === whatsApp.id 
																		? { ...w, habilitar_chats: newValue }
																		: w
																)
															);
															toast.success("Chats " + (newValue ? "habilitados" : "deshabilitados"));
														} catch (err) {
															toastError(err);
														}
													}}
													color="primary"
													size="small"
												/>
											</TableCell>
											<TableCell align="center">
												<IconButton
													size="small"
													onClick={() => handleEditWhatsApp(whatsApp)}
												>
													<Edit />
												</IconButton>

												<IconButton
													size="small"
													onClick={e => {
														handleOpenConfirmationModal("delete", whatsApp.id);
													}}
												>
													<DeleteOutline />
												</IconButton>
											</TableCell>
										</TableRow>
									))}
							</>
						)}
					</TableBody>
				</Table>
			</Paper>
		</MainContainer>
	);
};

export default Connections;
